import { Tabs } from "@/components";
import type { Tab } from "@/components/Tabs/types";
import type { PerformanceTabData } from "@/hooks/usePerformanceSectionData";
import { label } from "../Checkbox/Checkbox.css";
import type { ListVariants } from "../Tabs/Tabs.css";
import AnnualisedTab from "./AnnualisedTab";
import CumulativeTab from "./CumulativeTab";
import MonthlyTab from "./MonthlyTab";
import SummaryTab from "./SummaryTab";
import YearOnYearTab from "./YearOnYearTab";
import type { TabName } from "./types";

export default function PerformanceTabs({
	data,
	onTabChange,
	tabsToDisplay,
	activeTab,
	styleVariants,
	useDropdown,
	chart,
	className,
	note,
}: {
	data: PerformanceTabData;
	onTabChange?: (tab: TabName) => void;
	tabsToDisplay?: TabName[];
	activeTab?: TabName;
	styleVariants?: ListVariants;
	useDropdown?: boolean;
	chart?: React.ReactNode;
	note?: React.ReactNode;
	className?: string;
}) {
	if (!data) return null;
	const tabsToDisplayFilter = (obj: { key: TabName }) =>
		!tabsToDisplay ||
		tabsToDisplay.length === 0 ||
		tabsToDisplay.includes(obj.key);
	const tabs: Tab<string>[] = [
		{
			label: <div>Performance chart (%)</div>,
			key: "chart" as const,
		},
		{
			label: <div>Summary (%)</div>,
			key: "summary" as const,
		},
		{
			label: <div>Cumulative (%)</div>,
			key: "cumulative" as const,
		},
		{
			label: <div>Annualised (%)</div>,
			key: "annualised" as const,
		},
		{
			label: <div>Year on year (%)</div>,
			key: "yoy" as const,
		},
		{
			label: <div>Monthly (%)</div>,
			key: "monthly" as const,
		},
	].filter(tabsToDisplayFilter);
	const tabContents = [
		{
			key: "chart" as const,
			content: chart,
		},
		{
			key: "summary" as const,
			content: <SummaryTab data={data.summary} styleVariants={styleVariants} />,
		},
		{
			key: "cumulative" as const,
			content: (
				<>
					<CumulativeTab data={data.summary} styleVariants={styleVariants} />
					{note}
				</>
			),
		},
		{
			key: "annualised" as const,
			content: (
				<>
					<AnnualisedTab data={data.summary} styleVariants={styleVariants} />
					{note}
				</>
			),
		},
		{
			key: "yoy" as const,
			content: (
				<>
					<YearOnYearTab
						data={data?.yearOnYear}
						styleVariants={styleVariants}
					/>
					{note}
				</>
			),
		},
		{
			key: "monthly" as const,
			content: (
				<>
					<MonthlyTab data={data?.monthly} styleVariants={styleVariants} />
					{note}
				</>
			),
		},
	].filter(tabsToDisplayFilter);
	return (
		<Tabs
			label=""
			tabs={tabs}
			value={activeTab as string}
			tabContents={tabContents}
			onChange={(v) => v && onTabChange?.(v as TabName)}
			tabItemConfig={styleVariants}
			tabListConfig={styleVariants}
			className={className}
			useDropdown={useDropdown}
		/>
	);
}
